import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  alpha,
  Slider,
  styled,
  Card,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  BACKGROUND_GRADIENT,
  BACKGROUND_GRADIENT_TRANSPARENT,
  BUTTON_BG_COLOR,
} from './PropertySpotlightDialog';
import { parseCurrencyString } from '../../utils/formatPropertyPrice';
import { typographyClamps } from '../../utils/utils';
import { IsNotNullOrEmpty } from '../../utils/validation';

const DownPaymentSlider = styled(Slider)({
  color: '#5B9FD8',
  height: 8,
  padding: '15px 0',
  '& .MuiSlider-track': {
    border: 'none',
    borderRadius: 4,
  },
  '& .MuiSlider-rail': {
    backgroundColor: '#D5E5F3',
    opacity: 1,
    borderRadius: 4,
  },
  '& .MuiSlider-thumb': {
    height: 28,
    width: 28,
    backgroundColor: '#fff',
    border: '3px solid #5B9FD8',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: '0 2px 8px rgba(91, 159, 216, 0.3)',
    },
    '&::before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    display: 'none',
  },
});

interface PaymentInformationModelProps {
  carData: any;
  onSubmit: (downPayment: number) => void;
  onBack: () => void;
  gotoDealershipWebsite: () => void;
}

const PaymentInformationModel: React.FC<PaymentInformationModelProps> = ({
  carData,
  onBack,
  onSubmit,
  gotoDealershipWebsite,
}) => {
  const [downPaymentPercent, setDownPaymentPercent] = useState<number>(10);
  const [interestRate, setInterestRate] = useState<number>(6.8);

  const getNumericAmount = (input?: string | number | null): number => {
    if (input === null || input === undefined || input === '') return 0;
    const amount = Number(String(input).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(amount) ? amount : 0;
  };

  const listPrice = getNumericAmount(carData?.listPrice ?? carData?.price);
  const parsedPrice = parseCurrencyString(listPrice);
  const loanTermYears = 30;
  const loanTermMonths = loanTermYears * 12;
  const downPaymentAmount = Math.round(listPrice * (downPaymentPercent / 100));
  const loanAmount = Math.max(listPrice - downPaymentAmount, 0);
  const monthlyRate = interestRate / 100 / 12;
  const principalInterest =
    loanAmount <= 0
      ? 0
      : monthlyRate > 0
        ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, loanTermMonths)) /
        (Math.pow(1 + monthlyRate, loanTermMonths) - 1)
        : loanAmount / loanTermMonths;
  const monthlyTax = getNumericAmount(carData?.taxAnnualAmount ?? carData?.taxAmount) / 12;

  const getMonthlyHoa = () => {
    const fee = getNumericAmount(carData?.association?.associationFee);
    const frequency = String(
      carData?.association?.associationFrequency ||
      carData?.association?.associationFeeFrequency ||
      '',
    ).toLowerCase();

    if (!fee) return 0;
    if (frequency.includes('annual') || frequency.includes('year')) return fee / 12;
    if (frequency.includes('quarter')) return fee / 3;
    if (frequency.includes('semi')) return fee / 6;
    if (frequency.includes('week')) return (fee * 52) / 12;
    return fee;
  };

  const monthlyHOA = getMonthlyHoa();
  const totalMonthlyPayment = principalInterest + monthlyTax + monthlyHOA;
  const propertyDetails = [
    carData?.bedsTotal || carData?.bedrooms
      ? `${carData?.bedsTotal || carData?.bedrooms} beds`
      : null,
    carData?.bathroomsTotalInteger || carData?.bathroomsFull
      ? `${carData?.bathroomsTotalInteger || carData?.bathroomsFull} baths`
      : null,
    carData?.livingArea || carData?.area
      ? `${Number(carData?.livingArea || carData?.area).toLocaleString()} sq ft`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const handleDownPaymentChange = (event: Event, newValue: number | number[]) => {
    setDownPaymentPercent(newValue as number);
  };

  const handleInterestRateChange = (event: Event, newValue: number | number[]) => {
    setInterestRate(newValue as number);
  };

  const formatCurrency = (val: number): string => {
    return `$${Math.round(val).toLocaleString()}`;
  };

  const formatEMIAmount = (amount: number) => {
    const parsedAmount = parseCurrencyString(amount.toString());
    return parsedAmount?.amount !== '-'
      ? `${parsedAmount?.symbolOrCode}${parsedAmount?.amount}`
      : '-';
  };

  const getDownPaymentView = ({ min = 3, max = 30, step = 1 }) => {
    return (
      <Box sx={{ width: '100%' }}>
        {/* Header with label and value */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            // mb: 1,
          }}
        >
          <Typography
            variant='body2'
            sx={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 400,
            }}
          >
            Down Payment
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 500,
            }}
          >
            {downPaymentPercent}% · {formatCurrency(downPaymentAmount)}
          </Typography>
        </Box>

        {/* Slider */}
        <DownPaymentSlider
          value={downPaymentPercent}
          onChange={handleDownPaymentChange}
          min={min}
          max={max}
          step={step}
          aria-label='Down Payment'
        />

        {/* Min and Max labels */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            // mt: 1,
          }}
        >
          <Typography
            variant='caption'
            sx={{
              color: '#999',
              fontSize: '14px',
            }}
          >
            {min}%
          </Typography>
          <Typography
            variant='caption'
            sx={{
              color: '#999',
              fontSize: '14px',
            }}
          >
            {max}%
          </Typography>
        </Box>
      </Box>
    );
  };

  const getInterestRateView = ({ min = 5, max = 10, step = 0.1 }) => (
    <Box sx={{ width: '100%', mt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='body2' sx={{ color: '#333', fontSize: '16px', fontWeight: 400 }}>
          Interest Rate
        </Typography>
        <Typography variant='body2' sx={{ color: '#333', fontSize: '16px', fontWeight: 500 }}>
          {interestRate.toFixed(1)}%
        </Typography>
      </Box>
      <DownPaymentSlider
        value={interestRate}
        onChange={handleInterestRateChange}
        min={min}
        max={max}
        step={step}
        aria-label='Interest Rate'
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant='caption' sx={{ color: '#999', fontSize: '14px' }}>{min}%</Typography>
        <Typography variant='caption' sx={{ color: '#999', fontSize: '14px' }}>{max}%</Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        pb: 3,
        overflowY: 'auto',
        borderRadius: { xs: 2, sm: 3 },
        flex: 1,
        background: BACKGROUND_GRADIENT,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3,
          py: 1,
          mb: 2,
          textAlign: 'center',
          background:
            'linear-gradient(to right, #B1CAFF, #ffffff, #ffffff, #ffffff)',
          borderBottom: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.light, 0.1),
        }}
      >
        <IconButton
          onClick={() => onBack()}
          sx={{ mr: 0.5, p: 0.5, pl: 0 }}
          aria-label='Go back'
        >
          <KeyboardBackspaceIcon sx={{ color: 'primary.light' }} />
        </IconButton>
        <Typography fontWeight={400} color={'primary.light'}>
          Back to Property
        </Typography>
      </Box>

      <Box sx={{ px: 2 }}>
        <Typography
          variant='h6'
          fontWeight={500}
          sx={{ mb: 1, textAlign: 'center' }}
        >
          Payments
        </Typography>

        <Card
          sx={{
            backgroundColor: '#fff',
            backgroundImage: BACKGROUND_GRADIENT_TRANSPARENT,
          }}
        >
          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 1,
              gap: 0.2,
              borderBottom: '1px solid',
              borderColor: (theme) => alpha(theme.palette.primary.light, 0.1),
            }}
          >
            <Typography fontSize='14px' fontWeight={500}>
              {carData?.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontSize: '16px', // 18px
                  fontWeight: 600,
                  // background: (theme: any) => theme.palette.gradients.primary,
                  // backgroundClip: 'text',
                  // WebkitBackgroundClip: 'text',
                  // WebkitTextFillColor: 'transparent',
                  // color: 'transparent',
                }}
              >
                {parsedPrice?.amount !== '-'
                  ? `${parsedPrice?.symbolOrCode}${parsedPrice?.amount}`
                  : '-'}
              </Typography>
              <Typography
                fontSize='12px'
                fontWeight={400}
                color={'text.secondary'}
              >
                {propertyDetails ? ` · ${propertyDetails}` : ''}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: (theme) => alpha(theme.palette.primary.light, 0.1),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
              }}
            >
              <Typography
                variant='h4'
                fontWeight={600}
              >{formatEMIAmount(Math.ceil(totalMonthlyPayment))}</Typography>
              <Typography
                variant='h6'
                fontWeight={400}
                sx={{
                  '& span': {
                    color: 'text.secondary',
                  },
                }}
              >
                &nbsp;/ mo <span>(est.)</span>
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: typographyClamps.caption,
                textAlign: 'center',
                color: 'text.secondary',
                mb: 1,
              }}
            >{`${loanTermYears} years · ${formatCurrency(downPaymentAmount)} down · ${interestRate.toFixed(1)}% APR`}</Typography>
            {getDownPaymentView({})}
            {getInterestRateView({})}
            <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
              {[
                { label: 'Principal + Interest', value: principalInterest },
                { label: 'Property Tax', value: monthlyTax },
                { label: 'HOA', value: monthlyHOA },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: '8px',
                    bgcolor: 'rgba(255,255,255,0.62)',
                    px: 1.25,
                    py: 0.75,
                  }}
                >
                  <Typography sx={{ color: 'text.secondary', fontSize: '12px' }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ color: '#1A1A2E', fontSize: '12px', fontWeight: 700 }}>
                    {formatCurrency(item.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
            }}
          >
            <Button
              fullWidth
              variant='contained'
              onClick={() => onSubmit(downPaymentAmount)}
              sx={{
                py: 1.5,
                color: '#ffffff',
                background: BUTTON_BG_COLOR,
                textTransform: 'none',
              }}
            >
              Request Payment Estimate
            </Button>

            {IsNotNullOrEmpty(
              carData?.dealer?.dealerSettings?.dealershipWebsite,
            ) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mt: 1,
                }}
                onClick={gotoDealershipWebsite}
              >
                <Typography
                  gutterBottom
                  fontSize={'12px'}
                  sx={{ mt: 1, textAlign: 'center' }}
                >
                  View full payment details on agent site
                </Typography>
                <ArrowForwardIcon fontSize='small' color='secondary' />
              </Box>
            )}
          </Box>
        </Card>

        <Typography fontSize={'11px'} sx={{ mt: 1, textAlign: 'center' }}>
          Estimates vary by credit, taxes, fees, and lender approval.
        </Typography>
      </Box>
    </Box>
  );
};

export default PaymentInformationModel;
